import {
  CalendarRange,
  PenLine,
  Repeat,
  Send,
  Sparkles,
  Target,
  Users,
  ListChecks,
} from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const ITEMS = [
  {
    icon: Target,
    title: "Ideal customer profile & buyer personas",
    desc: "Crystal-clear parameters for your highest-value accounts and the buyer titles that matter.",
    span: "md:col-span-4",
  },
  {
    icon: Users,
    title: "50 researched target accounts",
    desc: "Hand-researched companies with 1–2 decision-makers identified at each.",
    span: "md:col-span-2",
  },
  {
    icon: Sparkles,
    title: "A personal reason to reach out",
    desc: "A personalized reason to reach out for every account — not a mail-merge fill-in.",
    span: "md:col-span-2",
  },
  {
    icon: PenLine,
    title: "Three outreach angles, written for email and LinkedIn",
    desc: "Ready-to-send messaging in three distinct angles, tuned to your market.",
    span: "md:col-span-4",
  },
  {
    icon: Repeat,
    title: "A 4-touch follow-up sequence",
    desc: "A structured cadence so no thread dies after one message.",
    span: "md:col-span-3",
  },
  {
    icon: Send,
    title: "A live proof run",
    desc: "First touches sent and test calls made for you — real market reaction, not theory.",
    span: "md:col-span-3",
    accent: true,
  },
  {
    icon: ListChecks,
    title: "A simple pipeline tracker",
    desc: "A clean tracker of accounts, touches and replies that you keep forever.",
    span: "md:col-span-2",
  },
  {
    icon: CalendarRange,
    title: "A 30-day execution plan & one-page results snapshot",
    desc: "Exactly what to do for the next 30 days, backed by data from your live test.",
    span: "md:col-span-4",
  },
];

const Deliverables = () => (
  <section
    id="deliverables"
    data-testid="deliverables-section"
    className="scroll-mt-24 border-y border-line bg-white py-20 lg:py-28"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Reveal>
            <Eyebrow>What you get</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium leading-[1.18] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Eight deliverables. One working system.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="pb-2 font-mono text-xs uppercase tracking-[0.2em] text-smokeLight">
            Handed over on Day 7
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-6 lg:gap-6">
        {ITEMS.map((item, i) => (
          <Reveal key={item.title} delay={(i % 3) * 0.08} className={`col-span-1 ${item.span} h-full`}>
            <div
              data-testid={`deliverable-card-${String(i + 1).padStart(2, "0")}`}
              className={`group h-full rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_-24px_rgba(26,26,25,0.35)] sm:p-8 ${
                item.accent
                  ? "border-terra bg-terra text-cream"
                  : "border-line bg-cream text-ink"
              }`}
            >
              <div className="flex items-start justify-between">
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
                    item.accent
                      ? "bg-white/15 text-cream"
                      : "bg-terra-soft text-terra group-hover:bg-terra group-hover:text-white"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                </span>
                <span
                  className={`font-serif text-4xl ${
                    item.accent ? "text-white/25" : "text-ink/10"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-8 font-serif text-xl leading-snug sm:text-2xl">
                {item.title}
              </h3>
              <p
                className={`mt-2.5 text-sm leading-relaxed ${
                  item.accent ? "text-cream/85" : "text-smoke"
                }`}
              >
                {item.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Deliverables;
